import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FaBell, FaCheck } from 'react-icons/fa';
import { useNotifications } from '../../contexts/NotificationContext';
import NotificationItem from './NotificationItem';
import { useNavigate } from 'react-router-dom';

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const BellButton = styled.button`
  position: relative;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text};
  font-size: 18px;
  cursor: pointer;
  padding: 8px;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
  
  &:focus {
    outline: none;
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  background-color: ${({ theme }) => theme.colors.danger};
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: white;
  min-width: 280px;
  max-width: 400px;
  max-height: 400px;
  overflow-y: auto;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  z-index: 1000;
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  
  @media (max-width: 480px) {
    position: fixed;
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    max-width: 100%;
    min-width: 100%;
    border-radius: 12px 12px 0 0;
    max-height: 80vh;
    transform: ${({ $isOpen }) => ($isOpen ? 'translateY(0)' : 'translateY(100%)')};
    transition: transform 0.3s ease-in-out;
    display: block;
  }
`;

const DropdownHeader = styled.div`
  padding: 12px 16px;
  color: black;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 480px) {
    padding: 16px;
    position: sticky;
    top: 0;
    background: white;
    z-index: 1;
  }
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
`;

const MarkAllReadButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  font-size: 12px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  
  &:hover {
    text-decoration: underline;
  }
  
  &:focus {
    outline: none;
  }
`;

const NotificationList = styled.div`
  color: black;
  max-height: 320px;
  overflow-y: auto;
  
  @media (max-width: 480px) {
    max-height: calc(80vh - 120px); /* Adjust for header and footer */
  }
`;

const EmptyState = styled.div`
  padding: 20px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textLight};
`;

const ViewAllButton = styled.button`
  width: 100%;
  padding: 12px;
  background: none;
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundLight};
  }
  
  @media (max-width: 480px) {
    position: sticky;
    bottom: 0;
    background: white;
    padding: 16px;
  }
`;

const Overlay = styled.div`
  display: none;
  
  @media (max-width: 480px) {
    display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }
`;

const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { notifications, loading, unreadCount, markAllAsRead } = useNotifications();
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };
  
  const handleMarkAllRead = (e) => {
    e.stopPropagation();
    markAllAsRead();
  };
  
  const handleViewAll = () => {
    setIsOpen(false);
    // Navigate to notifications page if available
    // navigate('/notifications');
  };
  
  return (
    <DropdownContainer ref={dropdownRef}>
      <BellButton onClick={toggleDropdown} aria-label="Notifications">
        <FaBell />
        {unreadCount > 0 && <NotificationBadge>{unreadCount}</NotificationBadge>}
      </BellButton>
      
      <Overlay $isOpen={isOpen} onClick={() => setIsOpen(false)} />
      
      <DropdownMenu $isOpen={isOpen}>
        <DropdownHeader>
          <Title>Notifications</Title>
          {unreadCount > 0 && (
            <MarkAllReadButton onClick={handleMarkAllRead}>
              <FaCheck style={{ marginRight: '4px' }} />
              Mark all as read
            </MarkAllReadButton>
          )}
        </DropdownHeader>
        
        <NotificationList>
          {loading ? (
            <EmptyState>Loading notifications...</EmptyState>
          ) : notifications.length === 0 ? (
            <EmptyState>No notifications</EmptyState>
          ) : (
            notifications.slice(0, 5).map((notification) => (
              <NotificationItem key={notification.id} notification={notification} />
            ))
          )}
        </NotificationList>
        
        {notifications.length > 5 && (
          <ViewAllButton onClick={handleViewAll}>
            View all ({notifications.length})
          </ViewAllButton>
        )}
      </DropdownMenu>
    </DropdownContainer>
  );
};

export default NotificationDropdown; 